import { StackedBarChart } from '@/components/StackedBarChart';
import { AnimatedNumber, StatCard } from '@/components/StatCard';
import { Skeleton } from '@/components/ui/skeleton';
import { useConsumptionAlertSummary } from '@/hooks/useConsumptionAlerts';
import { use404Redirect } from '@/hooks/use404Redirect';
import { useDashboardBilling, useDashboardSimStock, useDashboardSummary } from '@/hooks/useDashboard';
import { useDefaultSite } from '@/hooks/useDefaultSite';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { fadeUp, stagger } from '@/lib/animations';
import { defaultColor, operatorColors, operatorImages } from '@/lib/constants';
import { LINE_STATUS_OPTIONS } from '@/lib/filter-options';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { motion } from 'framer-motion';
import {
  Activity,
  ArrowRight,
  BellRing,
  Building2,
  CreditCard,
  FileText,
  Package,
  ReceiptEuro,
  Wifi,
} from 'lucide-react';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate, formatEuros } from '@/lib/format';

// ─── Main ────────────────────────────────────────────────────────────────────

export function HomeView() {
  const { data: summary, isLoading: summaryLoading } = useDashboardSummary();
  const { data: simStock, isLoading: simLoading } = useDashboardSimStock();
  const { data: billing, isLoading: billingLoading } = useDashboardBilling();
  const { data: organization, error: organizationError } =
    useCurrentOrganization();
  use404Redirect(organizationError);
  const { tenantSlug } = useTenantSlug();
  const { currentSite, handleSiteChange } = useDefaultSite();
  const navigate = useNavigate();

  const linesPath = `/o/${tenantSlug}/lines`;
  const ordersPath = `/o/${tenantSlug}/orders`;
  const invoicesPath = `/o/${tenantSlug}/invoices`;
  const consumptionAlertsPath = `/o/${tenantSlug}/consumption-alerts`;

  // Same window as the alerts page default (current month), so the count here
  // matches the one shown once the user follows the link.
  const alertsWindow = useMemo(() => {
    const now = new Date();
    return {
      from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
      to: new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      ).toISOString(),
    };
  }, []);
  const { data: alertSummary } = useConsumptionAlertSummary({
    organizationId: organization?.id,
    from: alertsWindow.from,
    to: alertsWindow.to,
  });
  const alertCount = alertSummary?.total ?? 0;

  const billingMonthLabel =
    billing && billing.hasData
      ? format(new Date(billing.year, billing.month - 1, 1), 'MMMM yyyy', {
          locale: fr,
        })
      : '';
  const topOverageLines = billing?.topConsumingLines ?? [];

  // Card: this year's orders. Banner: orders still in flight, whatever the year.
  const orderStatusCounts = summary?.orders.statusCounts ?? {};
  const pendingCount = orderStatusCounts.pending ?? 0;
  const completedCount = orderStatusCounts.completed ?? 0;
  const cancelledCount = orderStatusCounts.cancelled ?? 0;
  const ordersTotal = summary?.orders.total ?? 0;
  const ordersYear = summary?.orders.year ?? new Date().getFullYear();

  const activeStatusCounts = summary?.orders.activeStatusCounts ?? {};
  const shippedCount = activeStatusCounts.shipped ?? 0;
  const inTreatmentCount =
    (activeStatusCounts.pending ?? 0) + (activeStatusCounts.processing ?? 0);
  const activeCount = shippedCount + inTreatmentCount;

  const goToOrdersByStatus = (
    status: 'pending' | 'completed' | 'cancelled'
  ) => {
    navigate(`${ordersPath}?status=${status}`);
  };

  const goToLinesByStatus = (statusValue: string) => {
    navigate(`${linesPath}?status=${encodeURIComponent(statusValue)}`);
  };

  const goToLinesByOperator = (operator: string) => {
    navigate(`${linesPath}?operator=${encodeURIComponent(operator)}`);
  };

  const goToLinesByPlan = (plan: string) => {
    navigate(`${linesPath}?plan=${encodeURIComponent(plan)}`);
  };

  const goToInvoicesByMonth = (month: string) => {
    navigate(`/o/${tenantSlug}/invoices?month=${month}`);
  };

  const goToSiteLines = (siteName: string) => {
    const site = organization?.sites?.find(
      (s: { id: string; name: string }) => s.name === siteName
    );
    if (!site) return;
    handleSiteChange(site.id, linesPath);
  };

  const recentMonths = (summary?.invoices.countsByMonth ?? []).slice(0, 3);

  const simTotal = simStock?.total ?? 0;
  const simByOperator = simStock?.byOperator ?? [];

  const networkStats = summary?.lines.networkStats ?? [];
  const planStats = summary?.lines.planStats ?? [];
  const siteStats = summary?.lines.siteStats ?? [];

  const maxPlan = Math.max(...(planStats.map((p) => p.count) || [1]), 1);

  const today = formatDate(new Date());

  if (summaryLoading) {
    return (
      <div className="space-y-8">
        <div>
          <Skeleton className="h-10 w-72 mb-2" />
          <Skeleton className="h-6 w-56" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="rounded-2xl bg-white p-6 shadow-sm space-y-4"
            >
              <Skeleton className="h-11 w-11 rounded-xl" />
              <Skeleton className="h-9 w-24" />
              <Skeleton className="h-4 w-32" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="rounded-2xl bg-white p-6 shadow-sm space-y-4"
            >
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-32 w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const statusCounts = summary?.lines.statusCounts ?? {};
  const statusBreakdown = LINE_STATUS_OPTIONS.filter(
    (opt) => (statusCounts[opt.value] ?? 0) > 0
  ).map((opt) => ({
    value: opt.value,
    label: opt.label.toLowerCase(),
    count: statusCounts[opt.value],
  }));

  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* ── Hero greeting ──────────────────────────── */}
      <motion.div variants={fadeUp}>
        <h1 className="text-4xl font-bold text-slate-900">
          Bonjour, {organization?.name}
        </h1>
        <p className="mt-2 text-lg text-slate-400">
          Voici votre vue d'ensemble du {today}
        </p>
      </motion.div>

      {/* ── Consumption alerts banner ───────────────── */}
      <motion.div variants={fadeUp}>
        {/* Same surface as the dashboard cards (white, rounded, shadowed):
            a flat pastel block washes out against the page background. The
            colour signal is carried by the icon tile and the left edge. */}
        <Link
          to={consumptionAlertsPath}
          className={`group flex items-center justify-between overflow-hidden rounded-2xl border-l-4 bg-white py-4 pr-6 pl-5 shadow-sm ring-1 transition-all hover:-translate-y-0.5 hover:shadow-md ${
            alertCount > 0
              ? 'border-rose-500 ring-rose-100 hover:ring-rose-200'
              : 'border-emerald-500 ring-slate-200/70 hover:ring-emerald-200'
          }`}
        >
          <div className="flex items-center gap-4">
            <div
              className={`flex h-11 w-11 items-center justify-center rounded-xl shadow-sm ${
                alertCount > 0 ? 'bg-rose-500' : 'bg-emerald-500'
              }`}
            >
              <BellRing className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {alertCount > 0 ? (
                  <>
                    <span className="text-base font-extrabold tabular-nums text-rose-600">
                      {alertCount}
                    </span>{' '}
                    alerte{alertCount > 1 ? 's' : ''} de consommation ce mois-ci
                  </>
                ) : (
                  'Aucune alerte de consommation ce mois-ci'
                )}
              </p>
              <p
                className={`text-xs ${
                  alertCount > 0 ? 'text-rose-600' : 'text-slate-400'
                }`}
              >
                {alertCount > 0
                  ? 'Voir les lignes concernées'
                  : 'Aucune ligne n’a atteint un seuil'}
              </p>
            </div>
          </div>
          <ArrowRight
            className={`h-5 w-5 transition-transform group-hover:translate-x-1 ${
              alertCount > 0 ? 'text-rose-400' : 'text-slate-300'
            }`}
          />
        </Link>
      </motion.div>

      {/* ── Active orders banner ────────────────────── */}
      {activeCount > 0 && (
        <motion.div variants={fadeUp}>
          <Link
            to={`/o/${tenantSlug}/orders`}
            className="flex items-center justify-between rounded-2xl bg-amber-50 px-6 py-4 transition-colors hover:bg-amber-100/70 group"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
                <Package className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  {activeCount} commande{activeCount > 1 ? 's' : ''} en cours
                </p>
                <p className="text-xs text-amber-600">
                  {shippedCount > 0 &&
                    `${shippedCount} expédiée${shippedCount > 1 ? 's' : ''} · `}
                  {inTreatmentCount > 0 && `${inTreatmentCount} en traitement`}
                </p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-amber-400 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      )}

      {/* ── Stat cards ─────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Activity}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          label="Lignes actives"
          value={summary?.lines.total || 0}
          onClick={() => navigate(linesPath)}
        >
          {statusBreakdown.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-x-1 gap-y-1 text-xs text-slate-400">
              {statusBreakdown.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToLinesByStatus(s.value);
                  }}
                  className="cursor-pointer rounded-md px-1.5 py-0.5 -mx-1 transition-colors hover:bg-slate-100"
                >
                  <span className="font-medium text-slate-600">{s.count}</span>{' '}
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </StatCard>

        <StatCard
          icon={Wifi}
          iconBg="bg-sky-50"
          iconColor="text-sky-600"
          label="Réseaux"
          value={networkStats.length}
        >
          {networkStats.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {networkStats.map((net) => (
                <button
                  key={net.name}
                  type="button"
                  onClick={() => goToLinesByOperator(net.name)}
                  className="cursor-pointer flex items-center gap-2 rounded-md bg-slate-50 px-2.5 py-1.5 transition-colors hover:bg-slate-100"
                >
                  <img
                    src={
                      operatorImages[net.name] ||
                      '/images/operators/default.svg'
                    }
                    alt={net.name}
                    className="h-6 w-6 object-contain"
                  />
                  <span className="text-xs font-medium text-slate-600">
                    {net.count}
                  </span>
                </button>
              ))}
            </div>
          )}
        </StatCard>

        <StatCard
          icon={Package}
          iconBg="bg-rose-50"
          iconColor="text-rose-600"
          label={`Commandes ${ordersYear}`}
          value={ordersTotal}
        >
          {ordersTotal > 0 && (
            <div className="mt-3 flex flex-wrap gap-x-1 gap-y-1 text-xs text-slate-400">
              {pendingCount > 0 && (
                <button
                  type="button"
                  onClick={() => goToOrdersByStatus('pending')}
                  className="cursor-pointer rounded-md px-1.5 py-0.5 -mx-1 transition-colors hover:bg-slate-100"
                >
                  <span className="font-medium text-slate-600">
                    {pendingCount}
                  </span>{' '}
                  en cours
                </button>
              )}
              {completedCount > 0 && (
                <button
                  type="button"
                  onClick={() => goToOrdersByStatus('completed')}
                  className="cursor-pointer rounded-md px-1.5 py-0.5 -mx-1 transition-colors hover:bg-slate-100"
                >
                  <span className="font-medium text-slate-600">
                    {completedCount}
                  </span>{' '}
                  terminées
                </button>
              )}
              {cancelledCount > 0 && (
                <button
                  type="button"
                  onClick={() => goToOrdersByStatus('cancelled')}
                  className="cursor-pointer rounded-md px-1.5 py-0.5 -mx-1 transition-colors hover:bg-slate-100"
                >
                  <span className="font-medium text-slate-600">
                    {cancelledCount}
                  </span>{' '}
                  annulées
                </button>
              )}
            </div>
          )}
        </StatCard>

        <StatCard
          icon={Building2}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
          label={
            currentSite ? 'Centre de facturation' : 'Centres de facturation'
          }
          // Scoped to one centre, the whole dashboard already describes it:
          // the backend returns no breakdown by site, so counting the entries
          // would read 0 while a centre is plainly selected.
          value={currentSite ? 1 : siteStats.length}
          onClick={
            !currentSite && siteStats.length > 0
              ? () => goToSiteLines(siteStats[0].name)
              : undefined
          }
        >
          <p className="mt-3 text-xs text-slate-400">
            {currentSite ? (
              <>
                <span className="font-medium text-slate-600">
                  {summary?.lines.total ?? 0}
                </span>{' '}
                lignes · {currentSite.name}
              </>
            ) : siteStats.length > 0 ? (
              <>
                <span className="font-medium text-slate-600">
                  {siteStats[0].count}
                </span>{' '}
                lignes · {siteStats[0].name}
              </>
            ) : (
              'Aucun site'
            )}
          </p>
        </StatCard>
      </div>

      {/* ── Facturation ────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* KPI facturation */}
        <motion.div
          variants={fadeUp}
          className="rounded-2xl bg-white p-6 shadow-sm"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50">
                <ReceiptEuro className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  Dernière facturation
                </h2>
                <p className="text-sm text-slate-400 capitalize">
                  {billingMonthLabel}
                </p>
              </div>
            </div>
            <Link
              to={invoicesPath}
              className="flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700"
            >
              Factures <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {billingLoading ? (
            <Skeleton className="mt-5 h-24 rounded-xl" />
          ) : !billing?.hasData ? (
            <p className="py-8 text-center text-sm text-slate-400">
              Aucune facturation disponible
            </p>
          ) : (
            <>
              <p className="mt-5 text-3xl font-extrabold text-slate-900">
                {formatEuros(billing.total)}
              </p>
              <p className="mt-1 text-sm text-slate-500">Total facturé</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-xs text-slate-400">Abonnements</p>
                  <p className="mt-0.5 text-sm font-semibold text-slate-900">
                    {formatEuros(billing.subscriptionTotal)}
                  </p>
                </div>
                <div className="rounded-xl bg-amber-50 p-3">
                  <p className="text-xs text-amber-600">Hors-forfait</p>
                  <p className="mt-0.5 text-sm font-semibold text-amber-700">
                    {formatEuros(billing.consumptionTotal)}
                  </p>
                </div>
              </div>
            </>
          )}
        </motion.div>

        {/* Dernières factures */}
        <motion.div
          variants={fadeUp}
          className="rounded-2xl bg-white p-6 shadow-sm"
        >
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                Dernières factures
              </h2>
              <p className="text-sm text-slate-400 mt-0.5">Factures par mois</p>
            </div>
            <Link
              to={invoicesPath}
              className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
            >
              Tout voir <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          {recentMonths.length > 0 ? (
            <div className="flex flex-col gap-3">
              {recentMonths.map(({ month, count }, i) => {
                const monthLabel = format(
                  new Date(month + '-01'),
                  'MMMM yyyy',
                  { locale: fr }
                );
                return (
                  <motion.button
                    type="button"
                    onClick={() => goToInvoicesByMonth(month)}
                    key={month}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.7 + i * 0.08 }}
                    className="cursor-pointer flex items-center gap-4 rounded-xl px-3 py-3 text-left transition-colors hover:bg-slate-50"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 shrink-0">
                      <FileText className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-slate-700 capitalize">
                        {monthLabel}
                      </span>
                    </div>
                    <span className="text-sm font-bold text-slate-900">
                      {count} facture{count > 1 ? 's' : ''}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-8 text-center">
              Aucune facture disponible
            </p>
          )}
        </motion.div>
      </div>

      {/* ── Classements ────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Top forfaits — Ranking list */}
        <motion.div
          variants={fadeUp}
          className="rounded-2xl bg-white p-6 shadow-sm"
        >
          <div className="mb-6">
            <h2 className="text-lg font-bold text-slate-900">Top forfaits</h2>
            <p className="text-sm text-slate-400 mt-0.5">
              Répartition par abonnement
            </p>
          </div>

          {planStats.length > 0 ? (
            <div className="flex flex-col gap-4">
              {planStats.slice(0, 3).map((plan, i) => (
                <motion.button
                  type="button"
                  onClick={() => goToLinesByPlan(plan.name)}
                  key={plan.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 + i * 0.08 }}
                  className="cursor-pointer group flex items-center gap-4 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                  <span className="text-2xl font-bold text-slate-200 w-8 text-right tabular-nums">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700 truncate">
                        {plan.name}
                      </span>
                      <span className="text-sm font-bold text-slate-900 ml-3">
                        {plan.count}
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                      <motion.div
                        className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-400"
                        initial={{ width: 0 }}
                        animate={{
                          width: `${(plan.count / maxPlan) * 100}%`,
                        }}
                        transition={{
                          duration: 1,
                          ease: 'easeOut',
                          delay: 0.5 + i * 0.1,
                        }}
                      />
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-8 text-center">
              Aucune donnée disponible
            </p>
          )}
        </motion.div>

        {/* Top lignes en dépassement */}
        <motion.div
          variants={fadeUp}
          className="rounded-2xl bg-white p-6 shadow-sm"
        >
          <h2 className="text-lg font-bold text-slate-900">
            Top lignes en dépassement
          </h2>
          <p className="text-sm text-slate-400">
            <span className="capitalize">{billingMonthLabel}</span>
          </p>

          {billingLoading ? (
            <Skeleton className="mt-4 h-40 rounded-xl" />
          ) : topOverageLines.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">
              Aucune ligne en dépassement ce mois-ci
            </p>
          ) : (
            <div className="mt-4 space-y-2">
              {topOverageLines.map((line, i) => (
                <button
                  key={line.mobileLineId}
                  type="button"
                  onClick={() => navigate(`${linesPath}/${line.mobileLineId}`)}
                  className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-slate-50"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-100 text-xs font-bold text-slate-500">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {line.number}
                    </p>
                    {line.name && (
                      <p className="truncate text-xs text-slate-400">
                        {line.name}
                      </p>
                    )}
                  </div>
                  <span className="text-sm font-semibold tabular-nums text-amber-700">
                    {formatEuros(line.amount)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* ── Stock SIM ──────────────────────────────── */}
      <motion.div
        variants={fadeUp}
        className="rounded-2xl bg-white p-6 shadow-sm"
      >
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Stock SIM</h2>
            <p className="text-sm text-slate-400 mt-0.5">
              Cartes SIM disponibles
            </p>
          </div>
          <Link
            to={`/o/${tenantSlug}/sim-stock`}
            className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
          >
            Gérer <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {simLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-full" />
          </div>
        ) : (
          <>
            <div className="text-3xl font-extrabold text-slate-900 mb-1">
              <AnimatedNumber value={simTotal} />
            </div>
            <p className="text-sm text-slate-400 mb-5">cartes en stock</p>

            {simByOperator.length > 0 ? (
              <StackedBarChart
                items={simByOperator.map((op) => ({
                  name: op.name,
                  count: op.count,
                  color: operatorColors[op.name] || defaultColor,
                }))}
                total={simTotal}
                height="h-3"
                showTotal={false}
                animationDelay={0.8}
              />
            ) : (
              <div className="flex flex-col items-center py-6">
                <CreditCard className="h-8 w-8 text-slate-200 mb-2" />
                <p className="text-sm text-slate-400">Aucune SIM en stock</p>
              </div>
            )}
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
